/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { GenericTable, GenericTableColumn } from "@ui-components/Table/GenericTable";
import { SkeletonText, SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { Port, KEPort } from "../../request/request";

export interface PortViewModalProps {
  isOpen: boolean;
  isLoading: boolean;
  port: Port | null;
  onClose: () => void;
}

export const PortViewModal: React.FC<PortViewModalProps> = ({
  isOpen,
  isLoading,
  port,
  onClose,
}) => {
  const [activeTab, setActiveTab] = React.useState<"basic" | "kePorts">("basic");
  
  // Check if KE Ports tab should be enabled
  const hasKEPorts = port && port.KEPorts && port.KEPorts.length > 0;
  
  const TABS = [
    { id: "basic", label: "Basic Information" },
    { id: "kePorts", label: "KE Ports Information" },
  ];
  
  // Reset to basic tab when modal opens or port changes
  React.useEffect(() => {
    setActiveTab("basic");
  }, [isOpen, port?.id]);
  
  // KE Ports table columns (read-only, no Actions column)
  const kePortsColumns: GenericTableColumn<KEPort>[] = [
    {
      header: "Subscription ID",
      accessor: "subscriptionId",
      renderCell: (row) => (
        <span className="text-sm text-semantic-text-secondary">{row.subscriptionId || "-"}</span>
      ),
    },
    {
      header: "Tenant ID",
      accessor: "tenantId",
      renderCell: (row) => (
        <span className="text-sm text-semantic-text-secondary">{row.tenantId || "-"}</span>
      ),
    },
    {
      header: "Name",
      accessor: "name",
      renderCell: (row) => (
        <span className="text-sm text-semantic-text-secondary">{row.name || "-"}</span>
      ),
    },
    {
      header: "Capacity",
      accessor: "capacity",
      renderCell: (row) => (
        <span className="text-sm text-semantic-text-secondary">{row.capacity || "-"}</span>
      ),
    },
  ];

  return (
    <GenericModal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="View Port Details"
      customWidth="max-w-6xl"
    >
      {isLoading ? (
        <div className="space-y-6">
          <div>
            <SkeletonBlock h={20} w="30%" className="mb-3" />
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 6 }).map((_, idx) => (
                <SkeletonText key={idx} lines={2} lineHeight={14} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        port && (
          <div className="space-y-6 min-h-[600px]">
            {/* Tabs */}
            <div className="border-b border-gray-200 mb-4">
              <nav className="flex space-x-4" aria-label="Tabs">
                {TABS.map((tab) => {
                  const disabled = tab.id === "kePorts" && !hasKEPorts;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => !disabled && setActiveTab(tab.id as "basic" | "kePorts")}
                      disabled={disabled}
                      className={`
                        px-3 py-2 text-sm font-medium focus:outline-none rounded-t
                        ${
                          activeTab === tab.id
                            ? "text-semantic-text-brand border-b-2 border-semantic-border-brand-primary bg-semantic-bg-secondary"
                            : disabled
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-gray-500 hover:text-gray-700"
                        }
                      `}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>
            
            {/* Basic Information Tab */}
            {activeTab === "basic" && (
              <GenericCard isCollapsible={false} className="p-5">
                <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                  Basic Information
                </h3>
              <div className="grid grid-cols-2 gap-4">
                <GenericInput
                  label="ID"
                  value={port.id || ""}
                  readOnly
                />
                <GenericInput
                  label="Port Number"
                  value={port.portNumber || ""}
                  readOnly
                />
                <GenericInput
                  label="Port Description"
                  value={port.portName || ""}
                  readOnly
                />
                <GenericInput
                  label="Port Type"
                  value={port.portType || ""}
                  readOnly
                />
                <GenericInput
                  label="Port Status"
                  value={port.portStatus || ""}
                  readOnly
                />
                <GenericInput
                  label="Client Assignation"
                  value={port.clientAssignation || ""}
                  readOnly
                />
                <GenericInput
                  label="Hardware Resource ID"
                  value={port.hardwareResourceId ? String(port.hardwareResourceId) : ""}
                  readOnly
                />
                <GenericInput
                  label="Hardware Hostname"
                  value={port.hardwareHostname || ""}
                  readOnly
                />
                <GenericInput
                  label="Datacenter Display Name"
                  value={port.datacenterDisplayName || ""}
                  readOnly
                />
              </div>
              {/* Boolean fields as tags */}
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="flex flex-col">
                  <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                    Available
                  </label>
                  <GenericTag
                    label={port.isAvailable ? "Yes" : "No"}
                    variant={port.isAvailable ? "success" : "default"}
                    size="small"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                    Shared
                  </label>
                  <GenericTag
                    label={port.isShared ? "Yes" : "No"}
                    variant={port.isShared ? "success" : "default"}
                    size="small"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                    Decommissioned
                  </label>
                  <GenericTag
                    label={port.decommissioned ? "Yes" : "No"}
                    variant={port.decommissioned ? "success" : "default"}
                    size="small"
                  />
                </div>
              </div>
            </GenericCard>
            )}
            
            {/* KE Ports Information Tab */}
            {activeTab === "kePorts" && hasKEPorts && (
              <GenericCard isCollapsible={false} className="p-5">
                <div className="flex justify-between mb-4">
                  <GenericInput
                    label="KE Port Count"
                    value={port.kePortCount ? String(port.kePortCount) : "0"}
                    readOnly
                  />
                </div>
                {port.KEPorts && port.KEPorts.length > 0 && (
                  <div className="mt-4">
                    <GenericTable
                      columns={kePortsColumns}
                      data={port.KEPorts}
                      width="100%"
                      maxTableBodyHeight="400px"
                      isFooterVisible={false}
                    />
                  </div>
                )}
              </GenericCard>
            )}
          </div>
        )
      )}

      <div className="flex justify-end mt-6">
        <GenericButton label="Close" variant="secondary" onClick={onClose} />
      </div>
    </GenericModal>
  );
};
